import { useState } from "react";
import {
  BUSINESS_DELETE_BLOCKER_KEYS,
  isBusinessInUse,
  type BusinessDeleteBlockers,
} from "@releve/shared";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DangerZone } from "@/components/ui/danger-zone";
import { useBusinessDeleteBlockers, useDeleteBusiness } from "@/hooks/useBusinesses";
import { useTranslation } from "@/hooks/useTranslation";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** "Referenced by 3 expenses, 1 contract." — the non-zero blocker counts, in display order. */
function describeBusinessBlockers(t: TranslateFn, blockers: BusinessDeleteBlockers): string {
  const parts = BUSINESS_DELETE_BLOCKER_KEYS.filter((key) => blockers[key] > 0).map((key) =>
    t(`businessDialog.dangerZone.reasons.${key}`, { count: blockers[key] }),
  );
  return parts.length > 0 ? t("businessDialog.dangerZone.referencedBy", { list: parts.join(", ") }) : "";
}

interface BusinessDangerZoneProps {
  business: { id: string; name: string };
  /** After a successful delete — the CALLER decides where to go (never navigates here). */
  onDeleted: () => void;
  disabled?: boolean;
}

/**
 * The business edit dialog's danger zone (edit mode, opt-in via
 * `CreateBusinessDialog`'s `showDangerZone`). Mirrors `PersonDangerZone`, but
 * a business has no archive: one referenced by anything (ledger, documents,
 * timesheets, memos, people links, contracts…) cannot be deleted at all.
 *
 * The blocker counts are fetched lazily (`GET …/delete-blockers`) when this
 * mounts; while they load the button is disabled. The DELETE route re-checks
 * and answers 409 `business_in_use` if a reference appeared meanwhile — the
 * hook toasts it and the counts are refetched so the reasons show.
 */
export function BusinessDangerZone({ business, onDeleted, disabled }: BusinessDangerZoneProps) {
  const { t } = useTranslation();
  const blockersQuery = useBusinessDeleteBlockers(business.id);
  const deleteBusiness = useDeleteBusiness();
  const [confirming, setConfirming] = useState(false);

  const blockers = blockersQuery.data?.delete_blockers;
  const inUse = blockers ? isBusinessInUse(blockers) : false;

  let description = t("businessDialog.dangerZone.deleteDescription");
  if (blockersQuery.isLoading) {
    description = t("businessDialog.dangerZone.checking");
  } else if (blockers && inUse) {
    description = `${describeBusinessBlockers(t, blockers)} ${t("businessDialog.dangerZone.inUseDescription")}`;
  }

  return (
    <>
      <DangerZone
        title={t("businessDialog.dangerZone.title")}
        actionTitle={t("businessDialog.dangerZone.deleteTitle")}
        description={description}
        buttonLabel={t("businessDialog.dangerZone.deleteButton")}
        onAction={() => setConfirming(true)}
        // A failed blocker read leaves the button enabled: the DELETE route
        // re-checks, so the guard never depends on this read.
        disabled={disabled || blockersQuery.isLoading || inUse || deleteBusiness.isPending}
      />
      {confirming && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setConfirming(false)}
          title={t("businessDialog.dangerZone.deleteConfirmTitle", { name: business.name })}
          description={t("businessDialog.dangerZone.deleteConfirmDescription")}
          confirmText={t("businessDialog.dangerZone.deleteButton")}
          cancelText={t("cancel")}
          variant="destructive"
          onConfirm={async () => {
            try {
              await deleteBusiness.mutateAsync(business.id);
              onDeleted();
            } catch {
              // useDeleteBusiness toasts the failure (incl. a late
              // `business_in_use`); the MutationCache reports it. Refresh the
              // counts so the zone shows why.
              void blockersQuery.refetch();
            }
          }}
        />
      )}
    </>
  );
}
